import { motion } from 'framer-motion'
import { Zap, Brain, Puzzle, Shield, Clock, Users, Globe, Lock } from 'lucide-react'

const CompetitiveAdvantages = () => {
  const advantages = [
    {
      icon: Zap,
      title: 'Velocidade',
      description: 'Processamento em tempo real.',
      color: 'accent',
      features: ['Análise instantânea', 'Processamento paralelo', 'Cache inteligente']
    },
    {
      icon: Brain,
      title: 'Inteligência Avançada',
      description: 'Múltiplos agentes de IA com alta precisão de resultados.',
      color: 'primary',
      features: ['Melhores tecnologias IA', 'Análise contextual', 'Aprendizado contínuo']
    },
    {
      icon: Puzzle,
      title: 'Integrações',
      description: 'Conecte-se com suas ferramentas favoritas sem complicações.',
      color: 'accent',
      features: ['Office 365 - OneDrive', 'Webhooks / API Rest', 'SDKs nativos']
    },
    {
      icon: Shield,
      title: 'Segurança Enterprise',
      description: 'Confiança inicia pela segurança.Seus dados protegidos com padrões de instituição financeira.',
      color: 'primary',
      features: ['Criptografia AES-256', 'SOC 2 Type II', 'GDPR compliance']
    }
  ]

  const additionalFeatures = [
    {
      icon: Clock,
      title: 'Disponibilidade 99.9%',
      description: 'SLA garantido com monitoramento 24/7'
    },
    {
      icon: Users,
      title: 'Suporte Premium',
      description: 'Equipe dedicada para clientes enterprise'
    },
    {
      icon: Globe,
      title: 'Multi-região',
      description: 'Deploy global para baixa latência'
    },
    {
      icon: Lock,
      title: 'Backup Automático',
      description: 'Redundância tripla com sincronização'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <section className="section-padding bg-white">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            TANDER SMART - Muito mais que análise
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            É a inteligência aplicada. Precisão, visão estratégica e entrega de resultados como ninguém no mercado.
          </p>
        </motion.div>

        {/* Main Advantages Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
        >
          {advantages.map((advantage, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              className="card p-6 group"
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-${advantage.color}-100 text-${advantage.color}-500 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <advantage.icon className="w-8 h-8" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {advantage.title}
              </h3>
              
              <p className="text-gray-600 mb-4 leading-relaxed">
                {advantage.description}
              </p>

              <ul className="space-y-2">
                {advantage.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                    <div className={`w-2 h-2 rounded-full bg-${advantage.color}-400`}></div>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-2xl p-8"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Recursos Adicionais
            </h3>
            <p className="text-gray-600">
              Funcionalidades que garantem uma experiência profissional e confiável.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white text-primary-500 mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                  {feature.title}
                </h4>
                <p className="text-gray-600 text-xs leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Technology Stack */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Potencialize suas ideias e sua empresa com soluções de última geração.
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-600">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
              <span>OpenAI GPT-4</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-accent-500 rounded-full"></div>
              <span>Google Gemini</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
              <span>Mistral AI</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-accent-500 rounded-full"></div>
              <span>Ollama</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
              <span>Whisper</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default CompetitiveAdvantages
